/**
 * Native Web Push helpers — no third-party SaaS.
 * Flow:
 *   1. Register the service worker (`sw.js` already handles `push` + `notificationclick`).
 *   2. Fetch VAPID public key from /api/push/public-key.
 *   3. Ask the browser for Notification permission.
 *   4. Call pushManager.subscribe({ userVisibleOnly: true, applicationServerKey }).
 *   5. POST the subscription to /api/push/subscribe so the backend can send pushes.
 */
import { api } from './api';

export const PUSH_SUPPORTED = typeof window !== 'undefined'
  && 'serviceWorker' in navigator
  && 'PushManager' in window
  && 'Notification' in window;

function urlB64ToUint8Array(base64) {
  const padding = '='.repeat((4 - base64.length % 4) % 4);
  const b = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(b);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

async function getSWRegistration() {
  if (!('serviceWorker' in navigator)) return null;
  // The app already registers sw.js at the root. Reuse it.
  let reg = await navigator.serviceWorker.getRegistration('/sw.js');
  if (!reg) reg = await navigator.serviceWorker.register('/sw.js');
  await navigator.serviceWorker.ready;
  return reg;
}

export async function getPushState() {
  if (!PUSH_SUPPORTED) {
    return { supported: false, permission: 'default', subscribed: false, available: false };
  }
  const perm = Notification.permission;
  const reg = await getSWRegistration();
  const sub = reg ? await reg.pushManager.getSubscription() : null;
  let available = false;
  try {
    const { data } = await api.get('/push/public-key');
    available = !!data.available && !!data.public_key;
  } catch {}
  return { supported: true, permission: perm, subscribed: !!sub, available };
}

export async function enablePush() {
  if (!PUSH_SUPPORTED) return { ok: false, reason: 'unsupported' };
  // 1. Ask permission (must be a user gesture)
  const perm = await Notification.requestPermission();
  if (perm !== 'granted') return { ok: false, reason: 'denied' };
  // 2. Fetch VAPID public key
  const { data: keyRes } = await api.get('/push/public-key');
  if (!keyRes.available || !keyRes.public_key) return { ok: false, reason: 'server_unavailable' };
  // 3. Subscribe via the push manager
  const reg = await getSWRegistration();
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlB64ToUint8Array(keyRes.public_key),
    });
  }
  // 4. Register the subscription with Jixxeo's backend
  const json = sub.toJSON();
  await api.post('/push/subscribe', {
    endpoint: json.endpoint,
    keys: json.keys,
    user_agent: navigator.userAgent,
  });
  return { ok: true };
}

export async function disablePush() {
  if (!PUSH_SUPPORTED) return { ok: false };
  const reg = await getSWRegistration();
  const sub = reg ? await reg.pushManager.getSubscription() : null;
  if (sub) {
    try { await api.post('/push/unsubscribe', { endpoint: sub.endpoint }); } catch {}
    await sub.unsubscribe();
  }
  return { ok: true };
}

export async function sendTestPush() {
  try { await api.post('/push/test'); return { ok: true }; }
  catch (e) { return { ok: false, error: e }; }
}
